'use client'
import Section from 'components/Section'
import TrustpilotRatingBadge from 'components/TrustpilotRatingBadge'
import { MediaImageStandard, Typography } from 'components/Contentful'
import { useMemoizedContentGetter } from 'utils/contentful'
import SITE_THEME from 'constants/websites'
import classNames from 'classnames'
import { mainProductConfig } from 'constants/mainProductConfig'
import { AchieveLink } from 'components/AchieveLink/AchieveLink'
import { Button } from '@achieve/ascend'
import Cookies from 'js-cookie'

const makeButtonStyles = (type = 'primary', website = SITE_THEME.ACHIEVE) => {
  const BUTTON_STYLE = {
    [SITE_THEME.FDR]: {
      primary: classNames(
        'bg-[#cb000e]',
        'hover:bg-[#7A0D0D]',
        'hover:text-[white]',
        'leading-[1.2]',
        'w-full',
        'px-4',
        'lg:w-auto',
        'lg:px-6'
      ),
      secondary: classNames('text-blue-600', 'w-full', 'lg:w-auto', 'hover:bg-[#EFF5FF]'),
    },
  }

  if (BUTTON_STYLE[website] && BUTTON_STYLE[website][type]) {
    return BUTTON_STYLE[website][type]
  }

  return undefined
}
const ProvenResultList = ({ list }) => {
  return (
    <ul
      className="list-none p-0 m-0  relative flex w-fit flex-col self-center lg:w-full lg:flex-row lg:justify-between lg:pt-0 lg:text-black"
      id="proven-result-list"
    >
      {list.map((item) => (
        <li
          className="relative after:content-[''] pt-8 after:absolute after:inset-x-0 after:mx-auto after:h-[2px] after:w-24 after:bg-[#21A1FF] last:after:hidden lg:pt-0 lg:after:hidden"
          key={`${item.tagline}`}
        >
          <Typography
            content={item.quantity}
            className="block text-3xl font-normal leading-10 lg:text-2xl lg:font-medium"
          />
          <Typography content={item.tagline} className="block pb-8 text-lg lg:pb-0" />
        </li>
      ))}
    </ul>
  )
}
function HeroBrand({ content }) {
  const siteEnv = process.env.NEXT_PUBLIC_SITE_ENVIRONMENT || 'achieve'
  const leadId = Cookies.get('leadId') || ''

  const {
    title,
    description,
    primaryCta,
    secondaryCta,
    image,
    trustpilotInfo = {},
    config = {},
    trustMarkersGrid = {},
    ReviewsSectionArray,
  } = useMemoizedContentGetter(content, [
    'title',
    'description',
    'primaryCta',
    'secondaryCta',
    'image',
    'trustpilotInfo',
    'config',
    'trustMarkersGrid',
    'ReviewsSectionArray',
  ])
  const mobileMarginY = config?.jsonContent?.mobilePadding
  const trustMarkers = trustMarkersGrid?.gridItemsCollection?.items || []
  const showTrustmarkersOnDesktop = config?.jsonContent?.showTrustmarkersOnDesktop ?? true
  const provenResultsList = trustMarkers.map((marker) => ({
    quantity: marker.title?.json?.content?.[0]?.content?.[0]?.value || '',
    tagline: marker.subtitle?.json?.content?.[0]?.content?.[0]?.value || '',
  }))

  const { outOf, link, color } = trustpilotInfo.jsonContent || {}
  const { ctaContainerId = '' } = config?.jsonContent || {}
  const { totalRating: rating, totalReviews } = ReviewsSectionArray?.jsonContent || {}
  const stringToNumber = (str) => {
    if (str) return Number(str.replace(/,/g, ''))
    return null
  }
  const ratingToNumber = stringToNumber(rating)
  const totalReviewsToNumber = stringToNumber(totalReviews)

  const makeTrackObject = (text = '', url = '') => ({
    click_id: `${mainProductConfig[siteEnv || 'achieve']?.['MainProduct']} | ${text}`,
    click_text: `${mainProductConfig[siteEnv || 'achieve']?.['MainProduct']} | ${text}`,
    nav_link_section: 'Hero Card',
    track_event: 'button_click',
    event_action: 'link_click',
    event_type: 'link_click',
    click_url: url,
  })

  const onButtonClick = (link = '') => {
    if (typeof window !== 'undefined') window.location.href = link
  }

  return (
    <Section data-testid="hero-brand-section" contain={false}>
      <>
        <div className="relative lg:flex">
          <div
            className={classNames(
              'absolute',
              'bottom-[-120px]',
              'left-4',
              'z-5',
              'mx-5',
              'p-6',
              'rounded-[10px]',
              'shadow-gray-400',
              'drop-shadow-xl',
              'bg-[white]',
              'lg:w-[60%]',
              'lg:flex',
              'lg:items-center',
              'lg:justify-center',
              'lg:relative',
              'lg:p-0',
              mobileMarginY && mobileMarginY,
              'lg:m-0',
              'lg:rounded-none',
              'lg:drop-shadow-none',
              'lg:relative',
              'lg:bottom-0'
            )}
          >
            <div className="lg:mr-16 xl:ml-16">
              <Typography
                className={classNames(
                  'font-bold',
                  'text-black',
                  'text-[32px]',
                  'text-center',
                  'leading-[1.4]',
                  'tracking-[.01em]',
                  'lg:text-[57px]',
                  'lg:tracking-[.02em]',
                  'lg:leading-[1.3]',
                  'lg:text-left',
                  'lg:mb-4'
                )}
                content={title.textContent}
              />
              <div className="my-4 text-center lg:my-0">
                <Typography
                  className={classNames(
                    'last:ml-1',
                    'text-[16px]',
                    'text-[#1F1F23]',
                    'text-center',
                    'inline',
                    'lg:text-[18px]',
                    'lg:font-normal',
                    'lg:leading-[1.4]',
                    'lg:text-left',
                    'lg:block',
                    'lg:last:ml-0'
                  )}
                  content={description.textContent}
                />
              </div>
              {(primaryCta || secondaryCta) && (
                <div
                  id={ctaContainerId || 'hero-brand-ctas'}
                  className="flex flex-col gap-[8px] lg:flex-row lg:gap-[24px] lg:mt-8"
                >
                  {primaryCta && (
                    <AchieveLink
                      noLink
                      track={makeTrackObject(
                        primaryCta?.linkText?.json.content[0]?.content[0]?.value,
                        primaryCta?.linkHref.replace('{leadId}', leadId)
                      )}
                    >
                      <Button
                        className={makeButtonStyles('primary', siteEnv)}
                        variant="contained"
                        onClick={() =>
                          onButtonClick(primaryCta?.linkHref.replace('{leadId}', leadId))
                        }
                        data-testid="hero-brand-primary-button"
                        id="hero-brand-primary-button"
                      >
                        {primaryCta?.linkText?.json.content[0]?.content[0]?.value}
                      </Button>
                    </AchieveLink>
                  )}
                  {secondaryCta && (
                    <>
                      <AchieveLink
                        noLink
                        track={makeTrackObject(
                          secondaryCta?.linkText?.json.content[0]?.content[0]?.value,
                          secondaryCta?.linkHref
                        )}
                      >
                        <Button
                          className={makeButtonStyles('secondary', siteEnv)}
                          variant="outlined"
                          onClick={() => onButtonClick(secondaryCta?.linkHref)}
                          data-testid="hero-brand-secondary-button"
                        >
                          {secondaryCta?.linkText?.json.content[0]?.content[0]?.value}
                        </Button>
                      </AchieveLink>
                    </>
                  )}
                </div>
              )}
              {ratingToNumber && totalReviews && outOf && link && (
                <div className="mt-4 lg:mt-8 lg:justify-self-start">
                  <TrustpilotRatingBadge
                    rating={ratingToNumber}
                    totalReviews={totalReviewsToNumber}
                    outOf={outOf}
                    link={link}
                    color={color}
                  />
                </div>
              )}
              {trustMarkers && trustMarkers.length > 0 && showTrustmarkersOnDesktop && (
                <>
                  <div className="hidden lg:flex flex-row gap-8 mt-6">
                    {trustMarkers.map((marker) => {
                      const title = marker.title?.json?.content?.[0]?.content?.[0]?.value || ''
                      const subtitle =
                        marker.subtitle?.json?.content?.[0]?.content?.[0]?.value || ''
                      return (
                        <div
                          key={marker?.sys?.id}
                          className="flex flex-col items-center min-w-[120px]"
                        >
                          <Typography className="font-semibold text-lg" content={title} />
                          <Typography className="text-sm text-gray-600" content={subtitle} />
                        </div>
                      )
                    })}
                  </div>
                </>
              )}
            </div>
          </div>
          <div className="h-[528px] relative lg:h-[600px] lg:w-[40%]">
            <MediaImageStandard
              className="object-[0_38%]! scale-x-[-1] lg:object-[0_36%]! lg:scale-x-[1]"
              content={image.mediaContent}
              objectFit="cover"
              priority
              alt={image.mediaAltText}
            />
          </div>
        </div>
        {trustMarkers && trustMarkers.length > 0 && provenResultsList && (
          <div
            className={
              'lg:hidden col-span-full flex flex-col  bg-[#041B93FF] text-center text-white lg:col-span-6 lg:bg-white lg:pt-9 lg:text-base xl:col-span-5 xl:col-start-2 pt-32'
            }
          >
            <Typography content={'Proven results over the years'} className="px-4 text-3xl " />
            <ProvenResultList list={provenResultsList} />
          </div>
        )}
      </>
    </Section>
  )
}

export { HeroBrand }
export default HeroBrand
